import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Wallet, CreditCard } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { addPayment, deletePayment } from "@/lib/api";
import { formatMoney, formatDate } from "@/lib/constants";

export const PaymentsSection = ({ client, methods, currency, terminals = [], onUpdate }) => {
  const cur = client.currency || currency || "USD";
  const [method, setMethod] = useState(methods[0] || "");
  const [terminal, setTerminal] = useState("");
  const [amount, setAmount] = useState("");
  const [pdate, setPdate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const payments = client.payments || [];
  const total = Number(client.total_amount) || 0;
  const paid = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const pending = total - paid;

  const register = async () => {
    const amt = parseFloat(amount);
    if (!method) {
      toast.error("Selecciona una forma de pago");
      return;
    }
    if (!amt || amt <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    setSaving(true);
    try {
      const updated = await addPayment(client.id, {
        method,
        terminal: terminal || "",
        amount: amt,
        date: pdate,
        note,
      });
      onUpdate?.(updated);
      setAmount("");
      setNote("");
      setTerminal("");
      toast.success("Pago registrado");
    } catch {
      toast.error("No se pudo registrar el pago");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (pid) => {
    try {
      const updated = await deletePayment(client.id, pid);
      onUpdate?.(updated);
      toast.success("Pago eliminado");
    } catch {
      toast.error("No se pudo eliminar el pago");
    }
  };

  return (
    <div className="space-y-3" data-testid="payments-section">
      <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2">
        <Wallet className="h-4 w-4 text-slate-500" /> Historial de Pagos
      </h3>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <p className="text-xs uppercase tracking-wide text-slate-400 font-medium">Total</p>
          <p className="font-heading text-lg font-bold text-slate-900" data-testid="payment-total">
            {formatMoney(total, cur)}
          </p>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-xs uppercase tracking-wide text-emerald-600 font-medium">Pagado</p>
          <p className="font-heading text-lg font-bold text-emerald-700" data-testid="payment-paid">
            {formatMoney(paid, cur)}
          </p>
        </div>
        <div
          className={`rounded-lg border p-3 ${
            pending > 0 ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"
          }`}
        >
          <p
            className={`text-xs uppercase tracking-wide font-medium ${
              pending > 0 ? "text-amber-600" : "text-slate-400"
            }`}
          >
            Saldo pendiente
          </p>
          <p
            className={`font-heading text-lg font-bold ${
              pending > 0 ? "text-amber-700" : "text-slate-900"
            }`}
            data-testid="payment-pending"
          >
            {formatMoney(pending, cur)}
          </p>
        </div>
      </div>

      {/* Add payment */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2.5">
        <div className="grid grid-cols-2 gap-2.5">
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="payment-method-select" className="h-9 bg-white">
              <SelectValue placeholder="Forma de pago" />
            </SelectTrigger>
            <SelectContent>
              {methods.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="Monto"
            data-testid="payment-amount-input"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-9 bg-white"
          />
          <Input
            type="date"
            data-testid="payment-date-input"
            value={pdate}
            onChange={(e) => setPdate(e.target.value)}
            className="h-9 bg-white"
          />
          <Input
            placeholder="Nota (opcional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="h-9 bg-white"
          />
          {method.toLowerCase().includes("terminal") && (
            <div className="col-span-2 pt-1">
              <label className="text-xs font-semibold text-blue-900 mb-1 flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5 text-blue-600" /> Terminal con la que se cobró:
              </label>
              <select
                data-testid="payment-terminal-select"
                value={terminal}
                onChange={(e) => setTerminal(e.target.value)}
                className="w-full h-9 rounded-md border border-blue-200 bg-white px-3 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Seleccionar Terminal --</option>
                {client.terminal && !terminals.some((t) => t.name === client.terminal) && (
                  <option value={client.terminal}>
                    {client.terminal} (Histórica de este expediente)
                  </option>
                )}
                {terminals.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name} {t.status === "bloqueada" ? " [BLOQUEADA]" : " (Activa)"}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <Button
          size="sm"
          className="w-full"
          data-testid="register-payment-button"
          onClick={register}
          disabled={saving}
        >
          <Plus className="h-4 w-4 mr-1.5" /> Registrar pago
        </Button>
      </div>

      {/* List */}
      <div className="space-y-2">
        {payments.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-1">Aún no hay pagos registrados.</p>
        )}
        {payments.map((p) => (
          <div
            key={p.id}
            data-testid="payment-item"
            className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-2.5"
          >
            <div className="h-8 w-8 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
              <Wallet className="h-4 w-4 text-slate-500" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-700 flex items-center flex-wrap gap-1.5">
                <span>{formatMoney(p.amount, cur)}</span>
                <span className="text-slate-400 font-normal">· {p.method}</span>
                {p.terminal && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200/60 px-1.5 py-0.5 rounded">
                    <CreditCard className="w-3 h-3 text-blue-500" /> {p.terminal}
                  </span>
                )}
              </p>
              <p className="text-xs text-slate-400">
                {formatDate(p.date)}
                {p.note ? ` · ${p.note}` : ""}
              </p>
            </div>
            <ConfirmDialog
              title="¿Eliminar pago?"
              description={`Se eliminará el pago de ${formatMoney(p.amount, cur)}.`}
              testId="confirm-delete-payment"
              onConfirm={() => remove(p.id)}
              trigger={
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-slate-400 hover:text-red-500"
                  data-testid="delete-payment-button"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
};
